import { MetricUnit } from '@/__generated__/graphql';
import { useSuspenseQuery } from '@apollo/client';
import GET_CHART_PREVIEW_DATA from '@features/deviceDataConfigurationModal/graphql/queries/getMathModelingChartPreviewData';
import { Trans, msg } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { Box, TextField, Typography } from '@mui/material';
import * as math from 'mathjs';
import { Suspense, useEffect, useMemo, type FC } from 'react';
import { FieldError } from 'react-hook-form';
import { AccessorsTable } from './AccessorsTable';
import { ChartPreview, ChartPreviewSerie } from './ChartPreview';

interface Props {
  deviceId: string;
  value: string;
  onChange: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  error: FieldError | undefined;
  setValidator: (validator: (expression: string) => void) => void;
}

export const SuspenseComponent: FC<Props> = ({
  deviceId,
  setValidator,
  value,
  onChange,
  error,
}) => {
  const { _ } = useLingui();

  useEffect(() => {
    setValidator((expression) =>
      math.evaluate(expression, {
        data: {
          angle: 0,
        },
      }),
    );
  }, []);

  return (
    <Box display="grid" gridTemplateColumns="minmax(0, 1fr) minmax(0, 1fr)">
      <Box
        borderRight={1}
        borderTop={1}
        borderColor="divider"
        py={2}
        pr={2}
        height={480}
        overflow="auto"
        className="custom-scrollbar"
      >
        <Typography variant="h6">
          <Trans>Modelamiento de ángulo</Trans>
        </Typography>
        <Typography>
          <Trans>Expresión matemática para calcular el ángulo</Trans>
        </Typography>
        <Typography>
          <Trans>Variables disponibles:</Trans>
        </Typography>
        <AccessorsTable
          metrics={[
            {
              deviceId,
              name: _(msg`Ángulo`),
              accessorKey: `data.angle`,
            },
          ]}
        />
        <TextField
          value={value}
          onChange={onChange}
          size="small"
          fullWidth
          multiline
          minRows={12}
          maxRows={30}
          label={_(msg`Expresión`)}
          error={!!error}
          helperText={error?.message}
          sx={{ mt: 2 }}
        />
      </Box>
      <Box borderTop={1} borderColor="divider" py={2} pl={2}>
        <Suspense>
          <PreviewData deviceId={deviceId} expression={value} />
        </Suspense>
      </Box>
    </Box>
  );
};

const PreviewData: FC<{ deviceId: string; expression: string }> = ({ deviceId, expression }) => {
  const { data } = useSuspenseQuery(GET_CHART_PREVIEW_DATA, {
    variables: {
      input: {
        ids: [deviceId],
      },
      metricsInput: {
        start: '-3d',
        window: '1h',
      },
    },
  });

  const series: ChartPreviewSerie[] = useMemo(() => {
    const device = data.devices.find((device) => device.id === deviceId);
    if (!device) return [];

    const metric = device.metrics.angle;

    const series: ChartPreviewSerie[] = [
      {
        data: metric.data,
        dataType: 'angle',
        name: 'angle',
        unit: metric.unit,
      },
    ];

    const calculatedData: number[][] = [];

    for (const [timestamp, value] of metric.data) {
      try {
        const scope = { data: { angle: value }, result: null as number | null };
        math.evaluate(expression, scope);
        if (scope.result != null) {
          const point = [timestamp, scope.result];
          calculatedData.push(point);
        }
      } catch (error) {
        console.error(error);
      }
    }

    series.unshift({
      data: calculatedData,
      dataType: 'angle',
      name: 'result-angle',
      unit: MetricUnit.Degree,
    });

    return series;
  }, [data, expression]);

  return <ChartPreview series={series} />;
};

export const GCalculatedAngleModelInput: FC<Props> = ({ ...props }) => (
  <Suspense>
    <SuspenseComponent {...props} />
  </Suspense>
);
